import { compact, keyBy } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';

import { TypeaheadMultiSelectInput } from '@/components/form/baseInputs/TypeaheadMultiSelectInput/TypeaheadMultiSelectInput';
import { useFormContext } from '@/components/react-hook-form';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { DispositiveProvisionTransferTaxKind } from '@/types/schema';
import { getPulidKind, PulidKind } from '@/utils/pulid';

import {
  DispositiveProvisionsFormRecipient,
  RecipientKind,
} from '../DispositiveProvisionsForm.types';
import {
  _getDefaultedKindForRecipient,
  GetDefaultedKindForRecipientParams,
} from '../DispositiveProvisionsFormRecipients/hooks/useDefaultRecipientTransferTaxKind';
import {
  SURVIVING_SPOUSE_SENTINEL,
  useRecipientOptions,
  UseRecipientOptionsParams,
} from '../hooks/useRecipientOptions';
import {
  SelectedRecipient,
  SplitDispositionFormShape,
} from './SplitDispositionModal.types';

interface SplitRecipientsSelectorProps {
  errorMessage?: string;
  hasStateTax?: boolean;
  /** Must be computed outside the split form's FormProvider to avoid context shadowing */
  recipientOptionsParams: UseRecipientOptionsParams;
}

/**
 * Determine the recipient kind from a recipient ID.
 */
function getRecipientKindFromId(recipientId: string): RecipientKind {
  if (recipientId === SURVIVING_SPOUSE_SENTINEL) {
    return RecipientKind.SurvivingSpouse;
  }

  const pulidKind = getPulidKind(recipientId);
  switch (pulidKind) {
    case PulidKind.ClientProfile:
      return RecipientKind.Individual;
    case PulidKind.Entity:
      return RecipientKind.Entity;
    case PulidKind.ClientOrganization:
      return RecipientKind.Organization;
    case PulidKind.TestamentaryEntity:
      return RecipientKind.TestamentaryEntity;
    default:
      return RecipientKind.Individual;
  }
}

export function SplitRecipientsSelector({
  errorMessage,
  hasStateTax = false,
  recipientOptionsParams,
}: SplitRecipientsSelectorProps) {
  const { control, setValue } = useFormContext<SplitDispositionFormShape>();
  const watchedRecipients = useWatch({ control, name: 'selectedRecipients' });
  const selectedRecipients = useMemo(
    () => watchedRecipients ?? [],
    [watchedRecipients]
  );

  const {
    typeaheadOptions: recipientOptions,
    clientProfileRecipients,
    entityRecipients,
    organizationRecipients,
    testamentaryEntityRecipients,
  } = useRecipientOptions(recipientOptionsParams);

  const { primaryClients } = useHouseholdDetailsContext();
  // The first dead primary client is the dying client for transfer tax kind defaulting
  const dyingClientId = recipientOptionsParams.deadPrimaryClientIds[0] ?? '';

  // Find display name for a given option value
  const getDisplayName = useCallback(
    (value: string): string =>
      recipientOptions.find((option) => option.value === value)?.display ??
      value,
    [recipientOptions]
  );

  const defaultTransferTaxKindParams: GetDefaultedKindForRecipientParams =
    useMemo(
      () => ({
        clientProfileRecipients,
        entityRecipients,
        organizationRecipients,
        testamentaryEntityRecipients,
        primaryClients: primaryClients ?? [],
        dyingClientId,
        hasStateTax,
      }),
      [
        clientProfileRecipients,
        entityRecipients,
        organizationRecipients,
        testamentaryEntityRecipients,
        primaryClients,
        dyingClientId,
        hasStateTax,
      ]
    );

  const selectedIds = useMemo(
    () => selectedRecipients.map((r) => r.id),
    [selectedRecipients]
  );
  const selectedOptions = useMemo(() => {
    const optionsByValue = keyBy(recipientOptions, 'value');
    return compact(selectedIds.map((id) => optionsByValue[id]));
  }, [recipientOptions, selectedIds]);

  const handleChange = useCallback(
    (_event: unknown, newOptions: Array<{ value: string }>) => {
      const newSelectedIds = newOptions.map((option) => option.value);

      // Build updated recipients list: keep existing recipients that are still
      // selected and add new ones
      const updatedRecipients: SelectedRecipient[] = [];

      for (const id of newSelectedIds) {
        // Keep existing recipient if already selected (preserves transferTaxKind edits)
        const existing = selectedRecipients.find((r) => r.id === id);
        if (existing) {
          updatedRecipients.push(existing);
          continue;
        }

        // New recipient - auto-populate transfer tax kind
        const defaultKind = _getDefaultedKindForRecipient(
          { recipientId: id } as DispositiveProvisionsFormRecipient,
          defaultTransferTaxKindParams
        );

        updatedRecipients.push({
          id,
          displayName: getDisplayName(id),
          kind: getRecipientKindFromId(id),
          transferTaxKind:
            defaultKind ?? ('' as DispositiveProvisionTransferTaxKind | ''),
        });
      }

      setValue('selectedRecipients', updatedRecipients);
    },
    [defaultTransferTaxKindParams, getDisplayName, selectedRecipients, setValue]
  );
  const [inputValue, setInputValue] = useState('');

  return (
    <TypeaheadMultiSelectInput
      label="Recipient"
      hideLabel
      values={selectedOptions}
      options={recipientOptions.filter((option) => !option.disabled)}
      onChange={handleChange}
      errorMessage={errorMessage}
      placeholder="Search recipients"
      inputValue={inputValue}
      onInputChange={setInputValue}
    />
  );
}
